import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { useSettings } from '@/context/SettingsContext';
import { useAuth } from '@/context/AuthContext';
import SecuritySettingsModal from '@/components/admin/SecuritySettingsModal';
import { Settings as Cog, User, Palette, ServerCog, Save, Mail, Lock, Database, AlertCircle, CheckCircle2, Image as ImageIcon, ShieldCheck, Globe2, ScrollText } from 'lucide-react';

const TABS = [
  { id: 'profile',  label: 'Profil',           icon: User },
  { id: 'brand',    label: 'Marka',            icon: Palette },
  { id: 'security', label: 'Güvenlik',         icon: ShieldCheck },
  { id: 'system',   label: 'Sistem',           icon: ServerCog },
];

export default function SettingsPage() {
  const [tab, setTab] = useState('profile');

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3 mb-6" data-testid="settings-page-title">
          <Cog className="text-amber-500" /> Ayarlar
        </h1>

        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Tabs */}
          <div className="flex border-b border-slate-800 bg-slate-950/60">
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-bold transition border-b-2 ${
                    isActive
                      ? 'text-amber-400 border-amber-500 bg-slate-900/40'
                      : 'text-gray-500 border-transparent hover:text-white hover:bg-slate-800/30'
                  }`}
                  data-testid={`settings-tab-${t.id}`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-6">
            {tab === 'profile'  && <ProfileTab />}
            {tab === 'brand'    && <BrandTab />}
            {tab === 'security' && <SecurityTab />}
            {tab === 'system'   && <SystemTab />}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

/* ---------------- Profile Tab ---------------- */

function ProfileTab() {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdState, setPwdState] = useState({ saving: false, ok: false, err: '' });

  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [emailState, setEmailState] = useState({ saving: false, ok: false, err: '' });

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdState({ saving: true, ok: false, err: '' });
    if (pwd.next.length < 8) {
      setPwdState({ saving: false, ok: false, err: 'Yeni şifre en az 8 karakter olmalı.' });
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdState({ saving: false, ok: false, err: 'Yeni şifreler eşleşmiyor.' });
      return;
    }
    try {
      await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: '', next: '', confirm: '' });
      setPwdState({ saving: false, ok: true, err: '' });
      setTimeout(() => setPwdState(s => ({ ...s, ok: false })), 3000);
    } catch (err) {
      setPwdState({ saving: false, ok: false, err: err?.response?.data?.detail || 'Şifre güncellenemedi.' });
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailState({ saving: true, ok: false, err: '' });
    try {
      await api.post('/auth/update-email', emailForm);
      setEmailForm({ current_password: '', new_email: '' });
      setEmailState({ saving: false, ok: true, err: '' });
      setTimeout(() => setEmailState(s => ({ ...s, ok: false })), 3000);
      // Auth context'i yenilemesi gerek; basit yol: sayfa reload (kullanıcı hala oturumda)
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      setEmailState({ saving: false, ok: false, err: err?.response?.data?.detail || 'E-posta güncellenemedi.' });
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-profile-tab">
      <div>
        <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2"><User size={16} className="text-amber-500" /> Hesap Bilgileri</h2>
        <p className="text-xs text-gray-500 mb-4">Mevcut admin: <span className="text-amber-400 font-mono">{user?.email}</span> ({user?.role})</p>
      </div>

      {/* E-posta güncelleme */}
      <form onSubmit={submitEmail} className="bg-slate-950/50 border border-slate-800 rounded-xl p-5 space-y-4" data-testid="settings-email-form">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><Mail size={14} className="text-amber-500" /> E-posta Güncelle</h3>

        <div>
          <label className="text-[11px] font-bold text-gray-500 uppercase">Yeni E-posta</label>
          <input
            required type="email"
            placeholder="yeni@admin.com"
            className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
            value={emailForm.new_email}
            onChange={e => setEmailForm({ ...emailForm, new_email: e.target.value })}
            data-testid="settings-new-email-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-bold text-gray-500 uppercase">Mevcut Şifre (doğrulama için)</label>
          <input
            required type="password"
            placeholder="••••••••"
            className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
            value={emailForm.current_password}
            onChange={e => setEmailForm({ ...emailForm, current_password: e.target.value })}
            data-testid="settings-email-current-pwd-input"
          />
        </div>

        {emailState.err && <Banner kind="error" text={emailState.err} />}
        {emailState.ok  && <Banner kind="ok"    text="E-posta güncellendi. Yeniden giriş yapmanız gerekebilir." />}

        <button type="submit" disabled={emailState.saving} className="w-full h-11 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-60 transition flex items-center justify-center gap-2" data-testid="settings-email-save">
          <Save size={14} /> {emailState.saving ? 'Kaydediliyor…' : 'E-postayı Güncelle'}
        </button>
      </form>

      {/* Şifre değiştirme */}
      <form onSubmit={submitPwd} className="bg-slate-950/50 border border-slate-800 rounded-xl p-5 space-y-4" data-testid="settings-password-form">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><Lock size={14} className="text-amber-500" /> Şifreyi Değiştir</h3>

        <div>
          <label className="text-[11px] font-bold text-gray-500 uppercase">Mevcut Şifre</label>
          <input
            required type="password"
            placeholder="••••••••"
            className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
            value={pwd.current}
            onChange={e => setPwd({ ...pwd, current: e.target.value })}
            data-testid="settings-current-pwd-input"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-bold text-gray-500 uppercase">Yeni Şifre</label>
            <input
              required type="password" minLength="8"
              placeholder="En az 8 karakter"
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
              value={pwd.next}
              onChange={e => setPwd({ ...pwd, next: e.target.value })}
              data-testid="settings-new-pwd-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-500 uppercase">Yeni Şifre Tekrar</label>
            <input
              required type="password" minLength="8"
              placeholder="Yeni şifreyi tekrar girin"
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm"
              value={pwd.confirm}
              onChange={e => setPwd({ ...pwd, confirm: e.target.value })}
              data-testid="settings-confirm-pwd-input"
            />
          </div>
        </div>

        {pwdState.err && <Banner kind="error" text={pwdState.err} />}
        {pwdState.ok  && <Banner kind="ok"    text="Şifreniz başarıyla güncellendi." />}

        <button type="submit" disabled={pwdState.saving} className="w-full h-11 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-60 transition flex items-center justify-center gap-2" data-testid="settings-pwd-save">
          <Save size={14} /> {pwdState.saving ? 'Kaydediliyor…' : 'Şifreyi Güncelle'}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Brand Tab ---------------- */

const PRESET_COLORS = [
  { name: 'Aura Gold',  value: '#F5A623' },
  { name: 'Royal Red',  value: '#E11D48' },
  { name: 'Cyber Cyan', value: '#06B6D4' },
  { name: 'Neon Lime',  value: '#84CC16' },
  { name: 'Deep Purple',value: '#8B5CF6' },
  { name: 'Sunset',     value: '#F97316' },
];

function BrandTab() {
  const { panel_title, logo_url, primary_color, refresh } = useSettings();
  const [form, setForm] = useState({
    panel_title:   panel_title   || 'LUMEN',
    logo_url:      logo_url      || '',
    primary_color: primary_color || '#F5A623',
  });
  const [state, setState] = useState({ saving: false, ok: false, err: '' });

  // Sync form when context refreshes
  useEffect(() => {
    setForm({
      panel_title:   panel_title   || 'LUMEN',
      logo_url:      logo_url      || '',
      primary_color: primary_color || '#F5A623',
    });
  }, [panel_title, logo_url, primary_color]);

  const submit = async (e) => {
    e.preventDefault();
    setState({ saving: true, ok: false, err: '' });
    try {
      await api.put('/settings', form);
      await refresh();      // sidebar başlığı + document.title güncellensin
      setState({ saving: false, ok: true, err: '' });
      setTimeout(() => setState(s => ({ ...s, ok: false })), 2500);
    } catch (err) {
      setState({ saving: false, ok: false, err: err?.response?.data?.detail || 'Kaydedilemedi.' });
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="settings-brand-tab">
      <div>
        <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2"><Palette size={16} className="text-amber-500" /> Marka Özelleştirme</h2>
        <p className="text-xs text-gray-500">Panel başlığı, logosu ve ana rengini değiştirin. Değişiklikler kaydedildiğinde anında yansır.</p>
      </div>

      {/* Live preview */}
      <div className="bg-gradient-to-br from-slate-950 to-black border border-slate-800 rounded-xl p-5">
        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">Önizleme</div>
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-md grid place-items-center overflow-hidden shadow-lg"
            style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.primary_color}cc)`, boxShadow: `0 0 20px ${form.primary_color}40` }}
          >
            {form.logo_url
              ? <img src={form.logo_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              : <ImageIcon size={20} className="text-white/80" />}
          </div>
          <div className="leading-tight">
            <div className="font-bold text-xl text-white tracking-tight" data-testid="brand-preview-title">{form.panel_title || 'LUMEN'}</div>
            <div className="text-[10px] font-mono tracking-widest" style={{ color: form.primary_color }}>IPTV CONTROL</div>
          </div>
        </div>
      </div>

      <div>
        <label className="text-[11px] font-bold text-gray-500 uppercase">Panel Başlığı</label>
        <input
          required
          maxLength="40"
          placeholder="LUMEN, ACME TV, vb."
          className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
          value={form.panel_title}
          onChange={e => setForm({ ...form, panel_title: e.target.value })}
          data-testid="settings-panel-title-input"
        />
        <p className="text-[10px] text-gray-600 mt-1 ml-1">Sidebar'ın üstünde ve tarayıcı sekmesinde görüntülenir.</p>
      </div>

      <div>
        <label className="text-[11px] font-bold text-gray-500 uppercase">Logo URL (Opsiyonel)</label>
        <input
          placeholder="https://example.com/logo.png"
          className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono text-sm"
          value={form.logo_url}
          onChange={e => setForm({ ...form, logo_url: e.target.value })}
          data-testid="settings-logo-url-input"
        />
        <p className="text-[10px] text-gray-600 mt-1 ml-1">Boş bırakılırsa varsayılan kalkan ikonu kullanılır.</p>
      </div>

      <div>
        <label className="text-[11px] font-bold text-gray-500 uppercase block mb-2">Ana Renk</label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          {PRESET_COLORS.map(c => (
            <button
              type="button"
              key={c.value}
              onClick={() => setForm({ ...form, primary_color: c.value })}
              className={`h-12 rounded-xl border-2 transition flex items-center justify-center text-[10px] font-bold uppercase tracking-wider relative overflow-hidden ${form.primary_color === c.value ? 'border-white scale-105' : 'border-slate-800 hover:border-slate-600'}`}
              style={{ background: c.value }}
              title={c.name}
              data-testid={`settings-color-preset-${c.value}`}
            >
              <span className="text-white drop-shadow-md">{c.name}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="color"
            className="w-14 h-12 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer"
            value={form.primary_color}
            onChange={e => setForm({ ...form, primary_color: e.target.value })}
            data-testid="settings-color-picker"
          />
          <input
            type="text"
            pattern="^#[0-9A-Fa-f]{6}$"
            placeholder="#F5A623"
            className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-sm uppercase"
            value={form.primary_color}
            onChange={e => setForm({ ...form, primary_color: e.target.value })}
            data-testid="settings-color-hex-input"
          />
        </div>
      </div>

      {state.err && <Banner kind="error" text={state.err} />}
      {state.ok  && <Banner kind="ok"    text="Marka ayarları güncellendi. Yenileme ile tüm sayfalara yansır." />}

      <button type="submit" disabled={state.saving} className="w-full h-12 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-60 transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20" data-testid="settings-brand-save">
        <Save size={14} /> {state.saving ? 'Kaydediliyor…' : 'Marka Ayarlarını Kaydet'}
      </button>
    </form>
  );
}

/* ---------------- Security Tab (Phase 8.2) ---------------- */

function SecurityTab() {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null); // {geo, terms} summary

  const refresh = async () => {
    try {
      const [g, t, s] = await Promise.all([
        api.get('/saas/admin/geo-config'),
        api.get('/saas/admin/terms-config'),
        api.get('/saas/admin/geo-stats').catch(() => ({ data: null })),
      ]);
      setSnapshot({
        geo:   { ...g.data.config, detected_country: g.data.detected_country },
        terms: { ...t.data.config, stats: t.data.stats },
        stats: s.data || null,
      });
    } catch (e) {
      setSnapshot({ error: e?.response?.data?.detail || 'Yüklenemedi.' });
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="settings-security-tab">
      <div>
        <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2">
          <ShieldCheck size={16} className="text-amber-500" /> Güvenlik Yapılandırması
        </h2>
        <p className="text-xs text-gray-500">
          Geo-Shield (ülke koruması) ve sözleşme sürümü dayatması. Tüm değişiklikler audit log'a kaydedilir.
        </p>
      </div>

      {/* Quick status cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Geo card */}
        <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-5" data-testid="security-geo-card">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Globe2 size={14} className="text-amber-500" />
              <h3 className="text-sm font-bold text-white">Geo-Shield</h3>
            </div>
            <span
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                snapshot?.geo?.enabled
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                  : 'bg-slate-700/40 text-gray-400 border border-slate-700'
              }`}
              data-testid="security-geo-status"
            >
              {snapshot?.geo?.enabled ? 'AKTİF' : 'PASİF'}
            </span>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Algılanan ülke</span>
              <span className="font-mono font-bold text-amber-400">{snapshot?.geo?.detected_country || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">İzin verilenler</span>
              <span className="font-mono text-white">
                {(snapshot?.geo?.allowed_countries || []).join(', ') || <span className="text-gray-600">— (yok)</span>}
              </span>
            </div>
            {snapshot?.geo?.updated_at && (
              <div className="flex justify-between text-[10px]">
                <span className="text-gray-600">Son güncelleme</span>
                <span className="font-mono text-gray-500">
                  {new Date(snapshot.geo.updated_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Terms card */}
        <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-5" data-testid="security-terms-card">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <ScrollText size={14} className="text-amber-500" />
              <h3 className="text-sm font-bold text-white">Sözleşme Sürümü</h3>
            </div>
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40"
              data-testid="security-terms-version"
            >
              {snapshot?.terms?.current_version || '—'}
            </span>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Toplam müşteri</span>
              <span className="font-bold text-white">{snapshot?.terms?.stats?.total_customers ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Onay bekleyen</span>
              <span className="font-bold text-amber-400">{snapshot?.terms?.stats?.pending_acceptance ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Güncel onay</span>
              <span className="font-bold text-emerald-400">{snapshot?.terms?.stats?.accepted_current_version ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 9 — Geo block telemetry counters */}
      {snapshot?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="security-geo-stats">
          <StatPanel label="Son 30g blok" value={snapshot.stats.blocks?.last_30_days ?? 0} accent="amber" />
          <StatPanel label="Toplam blok" value={snapshot.stats.blocks?.all_time ?? 0} accent="white" />
          <StatPanel label="Waitlist (30g)" value={snapshot.stats.leads?.last_30_days ?? 0} accent="emerald" />
          <StatPanel label="Toplam waitlist" value={snapshot.stats.leads?.total ?? 0} accent="emerald" />
        </div>
      )}
      {snapshot?.stats?.blocks?.top_countries?.length > 0 && (
        <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4" data-testid="security-top-countries">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3">En Çok Engellenen Ülkeler (Son 30g)</div>
          <div className="flex flex-wrap gap-2">
            {snapshot.stats.blocks.top_countries.map((c) => (
              <div key={c.country} className="px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs flex items-center gap-2">
                <span className="font-mono font-bold text-amber-400">{c.country}</span>
                <span className="text-gray-500">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full h-12 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
        data-testid="security-open-modal"
      >
        <ShieldCheck size={16} /> Güvenlik Ayarlarını Düzenle
      </button>

      <SecuritySettingsModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}

/* ---------------- System Tab ---------------- */

function SystemTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings').then(res => setData(res.data)).catch(() => setData({}));
  }, []);

  const toggleBackup = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings', { backup_enabled: !data?.backup_enabled });
      setData(res.data || { ...data, backup_enabled: !data?.backup_enabled });
    } catch {
      alert('Yedekleme durumu güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-system-tab">
      <div>
        <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2"><ServerCog size={16} className="text-amber-500" /> Sistem Yönetimi</h2>
        <p className="text-xs text-gray-500">Sistem genelindeki yedekleme ve günlük durumu.</p>
      </div>

      {/* Backup toggle */}
      <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
              <Database size={14} className="text-amber-500" /> Otomatik Yedekleme
            </h3>
            <p className="text-xs text-gray-500">
              Müşteri / kanal / paket gibi temel koleksiyonların günlük olarak yedeklenmesi.
              {' '}
              <span className="text-amber-700/80 font-bold">(Phase 2 — şu an yalnızca tercih kaydediliyor)</span>
            </p>
          </div>
          <button
            type="button"
            onClick={toggleBackup}
            disabled={saving || !data}
            className={`relative w-14 h-8 rounded-full transition shrink-0 ${data?.backup_enabled ? 'bg-amber-500' : 'bg-slate-700'} disabled:opacity-50`}
            data-testid="settings-backup-toggle"
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform shadow-lg ${data?.backup_enabled ? 'translate-x-6' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Sistem kayıtları */}
      <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-5" data-testid="settings-system-info">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <ServerCog size={14} className="text-amber-500" /> Sistem Logları
        </h3>
        <div className="bg-black border border-slate-800 rounded-lg p-4 font-mono text-[11px] space-y-1.5 max-h-[280px] overflow-y-auto">
          <LogLine ts={isoNow(-1)}     level="ok"   msg="Auth router started" />
          <LogLine ts={isoNow(-1.5)}   level="ok"   msg="MongoDB connection established" />
          <LogLine ts={isoNow(-30)}    level="info" msg="Admin login from Web UI" />
          <LogLine ts={isoNow(-180)}   level="ok"   msg="EPG sync stub triggered" />
          <LogLine ts={isoNow(-3600)}  level="info" msg="Daily backup task scheduled (stub)" />
          <LogLine ts={isoNow(-7200)}  level="warn" msg="No real XMLTV parser configured (Phase 2)" />
        </div>
        <p className="text-[10px] text-gray-600 mt-3 italic">
          Gerçek log akışı Phase 2'de bağlanacak (ELK / Loki entegrasyonu).
        </p>
      </div>
    </div>
  );
}

/* ---------------- Helpers ---------------- */

function Banner({ kind, text }) {
  const tone = kind === 'ok'
    ? 'bg-emerald-500/10 border-emerald-700/40 text-emerald-400'
    : 'bg-red-500/10 border-red-700/40 text-red-400';
  const Icon = kind === 'ok' ? CheckCircle2 : AlertCircle;
  return (
    <div className={`flex items-center gap-2 text-xs p-3 border rounded-xl ${tone}`} data-testid={`settings-banner-${kind}`}>
      <Icon size={14} className="shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function StatPanel({ label, value, accent }) {
  const colorMap = {
    amber:   'text-amber-400',
    emerald: 'text-emerald-400',
    white:   'text-white',
  };
  return (
    <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colorMap[accent] || 'text-white'}`}>{value}</div>
    </div>
  );
}

function isoNow(secondsAgo = 0) {
  return new Date(Date.now() - secondsAgo * 1000).toLocaleTimeString('tr-TR', { hour12: false });
}

function LogLine({ ts, level, msg }) {
  const tone = level === 'ok'   ? 'text-green-400'  :
               level === 'warn' ? 'text-amber-400'  :
               level === 'err'  ? 'text-red-400'    :
                                  'text-blue-400';
  return (
    <div className="flex gap-3">
      <span className="text-gray-600 shrink-0">{ts}</span>
      <span className={`${tone} font-bold uppercase shrink-0`}>{level.padEnd(4, ' ')}</span>
      <span className="text-gray-300">{msg}</span>
    </div>
  );
}
